<i18n src="./resources/locales.json"></i18n>

<template>
  <section class="contact-list-container" ref="contactsList">
    <SmsSearchInput :placeholder="$t('search.text')" @input="value => (searchString = value)" />

    <v-list class="groups-list">
      <v-list-group
        v-for="(section, sectionIndex) in contactsSections"
        :key="sectionIndex"
        :ref="section.id"
        v-model="section.isExpanded"
        sub-group
        dense
        @click="handleShowListGroup(section.id)"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title
              v-text="`${section.title} (${(section.contacts || section.groups).length})`"
              class="text-gray"
            />
          </v-list-item-content>

          <v-list-item-action :always-show-actions="alwaysShowActions">
            <div v-if="useCustomActions">
              <slot name="sectionActions" :section="section"></slot>
            </div>
            <v-btn
              v-else-if="hasActions(section)"
              icon
              width="24"
              height="24"
              @click.stop="displayActionsMenu($event, section.actions, section)"
            >
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
            <v-tooltip v-else-if="section.restricted" right>
              <template v-slot:activator="{ on }">
                <v-icon class="dimmed" color="grey" small v-on="on">mdi-lock</v-icon>
              </template>
              <span>{{ $t('sections.tooltips.restrictedSectionActions') }}</span>
            </v-tooltip>
          </v-list-item-action>
        </template>

        <v-virtual-scroll
          :ref="`vScroll_${section.id}`"
          v-if="section.contacts && section.contacts.length"
          :bench="3"
          :items="section.contacts"
          item-height="48"
          :height="maxGroupHeight"
          class="contacts-list custom-scrollbar"
          :visible="section.isExpanded"
          @scroll="getVisibleAvatars()"
        >
          <template v-slot:default="{ item }">
            <SmsContactListItemV2
              :contact="item"
              :dark="$vuetify.theme.dark"
              @click.stop.prevent="openContact(item)"
              :always-show-actions="alwaysShowActions"
              :hide-contact-card="hideContactCard"
            >
              <template v-slot:actions>
                <div v-if="useCustomActions">
                  <slot name="contactActions" :contact="item"></slot>
                </div>
                <v-btn
                  v-else-if="hasActions(item)"
                  icon
                  width="24"
                  height="24"
                  @click.stop="displayActionsMenu($event, item.actions, item)"
                >
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
                <v-tooltip v-else right>
                  <template v-slot:activator="{ on }">
                    <v-icon class="dimmed" color="grey" small v-on="on">mdi-lock</v-icon>
                  </template>
                  <span>{{ $t('sections.tooltips.restrictedSectionActions') }}</span>
                </v-tooltip>
              </template>
            </SmsContactListItemV2>
          </template>
        </v-virtual-scroll>

        <SmsEmptyMessage
          v-else-if="section.contacts && !section.contacts.length"
          :dark="$vuetify.theme.dark"
          :message="getEmptyListMessage"
        />

        <v-list-group
          v-else-if="section.groups && section.groups.length"
          v-for="(group, groupIndex) in section.groups"
          :key="sectionIndex + '-' + groupIndex"
          v-model="group.isExpanded"
          sub-group
          dense
          :ref="group.id"
          class="sub-group"
          @click.stop="handleShowListGroup(section.id, group.id)"
        >
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title
                v-text="`${group.groupName} (${group.extendedContacts.length})`"
                class="text-gray white-space"
              />
            </v-list-item-content>

            <v-list-item-action :always-show-actions="alwaysShowActions">
              <div v-if="useCustomActions">
                <slot name="groupActions" :group="group"></slot>
              </div>
              <v-btn
                v-else-if="hasActions(group)"
                icon
                width="24"
                height="24"
                @click.stop="displayActionsMenu($event, group.actions, group)"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
              <v-tooltip v-else right>
                <template v-slot:activator="{ on }">
                  <v-icon class="dimmed" color="grey" small v-on="on">mdi-lock</v-icon>
                </template>
                <span>{{ $t('sections.tooltips.restrictedSectionActions') }}</span>
              </v-tooltip>
            </v-list-item-action>
          </template>

          <v-virtual-scroll
            :ref="`vScroll_groups_${group.id}`"
            v-if="group.extendedContacts && group.extendedContacts.length"
            :bench="3"
            :items="group.extendedContacts"
            item-height="48"
            :height="maxSubGroupHeight"
            class="contacts-list custom-scrollbar"
            :visible="group.isExpanded"
            @scroll="getVisibleAvatars()"
          >
            <template v-slot:default="{ item }">
              <SmsContactListItemV2
                :contact="item"
                :dark="$vuetify.theme.dark"
                @click.stop="openContact(item)"
                :always-show-actions="alwaysShowActions"
                :hide-contact-card="hideContactCard"
              >
                <template v-slot:actions>
                  <div v-if="useCustomActions">
                    <slot name="contactActions" :contact="item"></slot>
                  </div>
                  <v-btn
                    v-else-if="hasActions(section)"
                    icon
                    width="24"
                    height="24"
                    @click.stop="displayActionsMenu($event, item.actions, item, group)"
                  >
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                  <v-tooltip v-else right>
                    <template v-slot:activator="{ on }">
                      <v-icon class="dimmed" color="grey" small v-on="on">mdi-lock</v-icon>
                    </template>
                    <span>{{ $t('sections.tooltips.restrictedSectionActions') }}</span>
                  </v-tooltip>
                </template>
              </SmsContactListItemV2>
            </template>
          </v-virtual-scroll>

          <SmsEmptyMessage
            v-else-if="group.extendedContacts && !group.extendedContacts.length"
            :dark="$vuetify.theme.dark"
            :message="getEmptyGroupMessage"
          />
        </v-list-group>

        <SmsEmptyMessage
          v-else-if="section.groups && !section.groups.length"
          :dark="$vuetify.theme.dark"
          :message="getEmptyGroupMessage"
        />
      </v-list-group>
    </v-list>
  </section>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapActions, mapGetters, mapState } from 'vuex';
import SmsSearchInput from '@/components/SmsSearchInput/SmsSearchInput';
import ToastMixin from '@/mixins/ToastMixin';
import SmsContactListItemV2 from '@/components/SmsContactItemV2/SmsContactListItemV2';
import SmsEmptyMessage from '@/components/SmsEmptyMessage/SmsEmptyMessage';

export default {
  name: 'SmsContactList',

  components: {
    SmsContactListItemV2,
    SmsSearchInput,
    SmsEmptyMessage
  },

  mixins: [ToastMixin],

  props: {
    maxListHeight: {
      type: Number,
      default: -1
    },
    readOnlyList: {
      type: Boolean,
      default: false
    },
    useCustomActions: {
      type: Boolean,
      default: false
    },
    alwaysShowActions: {
      type: Boolean,
      default: false
    },
    hideContactCard: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      actions: {
        addContact: {
          label: this.$t('contacts.addNewContact'),
          action: this.addContactDialog
        },

        contacts: {
          edit: {
            label: this.$t('contacts.editContact'),
            action: this.onEditContact
          },
          delete: {
            label: this.$t('contacts.deleteContact'),
            action: this.onDeleteContact
          },
          favorite: {
            add: {
              label: this.$t('contacts.addToFavorites'),
              action: this.addFavorite
            },
            remove: {
              label: this.$t('contacts.removeFromFavorites'),
              action: this.removeFavorite
            }
          }
        },

        group: {
          section: {
            addGroup: {
              label: this.$t('sections.createNewGroup'),
              action: this.createGroup
            }
          },

          contacts: {
            remove: {
              label: this.$t('contacts.removeFromGroup'),
              action: this.removeContactFromCurrentGroup
            }
          },

          sendMessage: {
            label: this.$t('groups.broadcastMessage'),
            action: this.sendMessageToGroup
          },
          sendGroupMessage: {
            label: this.$t('groups.groupMessage'),
            action: this.sendGroupMessageToGroup
          },
          share: {
            label: this.$t('groups.shareGroup'),
            action: this.shareGroup
          },
          edit: {
            label: this.$t('groups.renameGroup'),
            action: this.editGroupName
          },
          add: {
            label: this.$t('groups.addPeopleToGroup'),
            action: this.addPeopleToGroup
          },
          remove: {
            label: this.$t('groups.deleteGroup'),
            action: this.deleteGroup
          }
        },

        divider: {}
      },

      isLoading: false,
      searchString: '',
      maxGroupHeight: 48,
      maxSubGroupHeight: 48,
      resizeObserver: null,
      localExpandedSection: 'favorites',
      localExpandedGroup: ''
    };
  },

  computed: {
    ...mapGetters('user', ['userLinkId']),
    ...mapState('user', { userId: state => state.userId }),
    ...mapGetters('preferences', ['getSettings', 'getFavoriteContacts']),
    ...mapState('preferences', {
      currentSectionId: state => state.settings.interfaceState.contactsExpansionStates.sectionId
    }),

    ...mapGetters('conversationsV2', ['activeConversation', 'getConversationById']),
    ...mapGetters('contactsV2', ['allContacts', 'findContacts', 'findCurrentUserContacts', 'findEnterpriseContacts']),
    ...mapGetters('groupsV2', ['allGroups', 'findGroupsIncludingContact']),

    currentSection() {
      return this.readOnlyList ? this.localExpandedSection : this.currentSectionId;
    },

    getEmptyListMessage() {
      return this.searchString
        ? this.$t('messages.emptySearch', { searchString: this.searchString })
        : this.$t('messages.emptyList');
    },

    getEmptyGroupMessage() {
      return this.searchString
        ? this.$t('messages.emptySearchGroup', { searchString: this.searchString })
        : this.$t('messages.emptyGroup');
    },

    currentUserContactsExtended() {
      return this.findCurrentUserContacts(this.searchString).map(this.extendedContact);
    },

    enterpriseContactsExtended() {
      return this.findEnterpriseContacts(this.searchString).map(this.extendedContact);
    },

    groupsIncludingContactExtended() {
      return this.findGroupsIncludingContact(this.searchString).map(group => {
        if (!group.isEnterpriseGroup) {
          group.actions = [
            this.actions.group.add,
            this.actions.group.edit,
            this.actions.group.remove,
            this.actions.group.share,
            this.actions.divider,
            this.actions.group.sendMessage,
            this.actions.group.sendGroupMessage,
          ];
        }
        group.extendedContacts = group.filteredContacts(this.searchString).map(this.extendedGroupContact);
        group.isExpanded = this.readOnlyList
          ? this.localExpandedGroup === group.id
          : this.getSettings.interfaceState.contactsExpansionStates.groupId === group.id;
        return group;
      });
    },

    totalContactsInGroups() {
      return this.groupsIncludingContactExtended.reduce((accumulator, group) => {
        return accumulator + group.contacts.length;
      }, 0);
    },

    allContactsExtended() {
      return this.findContacts(this.searchString).map(this.extendedContact);
    },

    favoriteContactsExtended() {
      return this.allContactsExtended.filter(contact => contact.isFavoriteContact);
    },

    contactsSections() {
      return [
        {
          id: 'favorites',
          title: this.$t('sections.favorites'),
          contacts: this.favoriteContactsExtended,
          restricted: false,
          actions: [],
          isExpanded: this.currentSection === 'favorites'
        },
        {
          id: 'myContacts',
          title: this.$t('sections.myContacts'),
          contacts: this.currentUserContactsExtended,
          restricted: false,
          actions: [this.actions.addContact],
          isExpanded: this.currentSection === 'myContacts'
        },
        {
          id: 'enterpriseContacts',
          title: this.$t('sections.enterpriseContacts'),
          contacts: this.enterpriseContactsExtended,
          restricted: true,
          actions: [],
          isExpanded: this.currentSection === 'enterpriseContacts'
        },
        {
          id: 'groups',
          title: this.$t('sections.groups'),
          groups: this.groupsIncludingContactExtended,
          restricted: false,
          actions: [this.actions.group.section.addGroup],
          isExpanded: this.currentSection === 'groups'
        },
        {
          id: 'all',
          title: this.$t('sections.all'),
          contacts: this.allContactsExtended,
          restricted: false,
          actions: [this.actions.addContact],
          isExpanded: this.currentSection === 'all'
        }
      ];
    }
  },

  mounted() {
    this.resizeObserver = new ResizeObserver(() => {
      this.setVirtualScrollerHeights(false);
    }).observe(this.$refs.contactsList);
    this.setVirtualScrollerHeights(false);
    this.$root.$on('open-contact', this.openContact);
  },

  beforeDestroy() {
    this.$root.$off('open-contact');
    delete this.resizeObserver;
  },

  watch: {
    'searchString.length'() {
      this.setVirtualScrollerHeights(false);
    },
    'allContactsExtended.length'() {
      this.setVirtualScrollerHeights(false);
    },
    totalContactsInGroups() {
      this.setVirtualScrollerHeights(false);
    }
  },

  methods: {
    ...mapActions('preferences', ['updateSettings', 'addContactToFavorites', 'removeContactFromFavorites']),
    ...mapActions('contactsV2', ['deleteUserContact', 'retrieveAvatars']),
    ...mapActions('conversationsV2', ['setActiveConversationId', 'startConversation']),
    ...mapActions('groupsV2', ['addUserGroup', 'editUserGroup', 'deleteUserGroup', 'removeContactFromGroup']),

    getActiveContactListRef() {
      const activeScroller = this.$el.querySelector('[visible].v-virtual-scroll');
      return activeScroller ? activeScroller.__vue__ : null;
    },

    extendedContact(contact) {
      if (!contact) return;

      let baseActions = [];
      if (contact.isUserContact && !contact.isAzureContact) {
        baseActions = [this.actions.contacts.edit, this.actions.contacts.delete];
      }

      if (contact.isFavoriteContact) {
        contact.actions = [...baseActions, this.actions.contacts.favorite.remove];
      } else {
        contact.actions = [...baseActions, this.actions.contacts.favorite.add];
      }

      return contact;
    },

    extendedGroupContact(contact) {
      const extendedContact = this.extendedContact(contact);
      extendedContact.actions.push(this.actions.divider);
      extendedContact.actions.push(this.actions.group.contacts.remove);
      return extendedContact;
    },

    /**
     * Get the visible avatars and add them to the contacts
     * @param contactsArray
     * @param sectionIndex
     */
    getVisibleAvatars: debounce(function () {
      const activeListRef = this.getActiveContactListRef();
      if (!activeListRef) return;

      const indexStart = activeListRef.firstToRender;
      const indexEnd = activeListRef.lastToRender;
      const contactsToQuery = activeListRef.items.slice(indexStart, indexEnd);

      this.retrieveAvatars(contactsToQuery);
    }, 250),

    handleShowListGroup(sectionId, groupId) {
      // Set the max heights & get visible avatars
      if (!groupId) {
        this.setMaxSectionHeight(sectionId);
      } else {
        this.setMaxGroupHeight(sectionId, groupId);
      }
      this.getVisibleAvatars();
    },

    setVirtualScrollerHeights(storeState = true) {
      const activeSection = this.contactsSections.find(section => section.isExpanded);
      if (!activeSection) return;
      const activeGroup = activeSection.groups ? activeSection.groups.find(group => group.isExpanded) : null;
      this.setMaxSectionHeight(activeSection.id, storeState);
      if (activeGroup) {
        this.setMaxGroupHeight(activeSection.id, activeGroup.id, storeState);
      }
      this.getVisibleAvatars();
    },

    setMaxSectionHeight(sectionId, storeState = true) {
      const MIN_HEIGHT = 48 * 3;
      const currentSection = this.contactsSections.find(section => section.id === sectionId);
      const vh =
        this.maxListHeight !== -1
          ? this.maxListHeight
          : Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      let availableSpace = vh - 120 - this.contactsSections.length * 28;
      if (availableSpace < MIN_HEIGHT) availableSpace = MIN_HEIGHT;
      const itemsSpace = currentSection.contacts?.length * 48 || Number.MAX_SAFE_INTEGER;
      this.maxGroupHeight = itemsSpace < availableSpace ? itemsSpace : availableSpace;
      if (storeState) {
        this.storeExpandedState(sectionId);
      }
    },

    setMaxGroupHeight(sectionId, groupId, storeState = true) {
      const currentSection = this.contactsSections.find(section => section.id === sectionId);
      const hasGroups = !!currentSection.groups;

      const MIN_HEIGHT = 48 * 3;
      let availableSpace = this.maxGroupHeight;
      if (hasGroups) availableSpace -= currentSection.groups.length * 28 || 0;

      if (availableSpace < MIN_HEIGHT) availableSpace = MIN_HEIGHT;
      const currentGroup = currentSection.groups.find(group => group.id === groupId);
      const itemsSpace = currentGroup?.contacts.length * 48 || MIN_HEIGHT;
      this.maxSubGroupHeight = itemsSpace < availableSpace ? itemsSpace : availableSpace;
      if (storeState) {
        this.storeExpandedState(sectionId, groupId);
      }
    },

    filteredGroupContacts(group) {
      return group.filteredContacts(this.searchString);
    },

    async storeExpandedState(sectionId = null, groupId = null) {
      await this.$nextTick();

      this.localExpandedSection = sectionId;
      this.localExpandedGroup = groupId;
      if (this.readOnlyList) return;

      const currentUserState = this.getSettings;
      let newSectionId, newGroupId;

      const currentSectionId = currentUserState.interfaceState.contactsExpansionStates.sectionId;
      const currentGroupId = currentUserState.interfaceState.contactsExpansionStates.groupId;

      if (groupId) {
        newSectionId = sectionId;
        newGroupId = currentGroupId === groupId ? null : groupId;
      } else {
        newSectionId = currentSectionId === sectionId ? null : sectionId;
        newGroupId = null;
      }

      currentUserState.interfaceState.contactsExpansionStates = {
        sectionId: newSectionId,
        groupId: newGroupId
      };

      this.isLoading = true;
      this.updateSettings(currentUserState).finally(() => {
        this.isLoading = false;
      });
    },

    /**
     * Verify if an item has an array of actions and if this has a length greater than 0
     * @param {Object} item - The item to verify if has actions.
     */
    hasActions(item) {
      return item.actions?.length > 0;
    },

    displayActionsMenu({ clientX, clientY }, actions, contact, group = null) {
      this.$root.$emit('open-context-menu', {
        contact,
        group,
        actions,
        positionX: clientX,
        positionY: clientY
      });
    },

    createGroup() {
      this.$root.$emit('open-group-dialog');
    },

    editGroupName(currentGroup) {
      this.$root.$emit('open-group-dialog', currentGroup.lean);
    },

    addPeopleToGroup(currentGroup) {
      this.$root.$emit('open-add-contact-to-group-dialog', currentGroup);
    },

    removeContactFromCurrentGroup(contact, group) {
      this.removeContactFromGroup({ contact, group }).finally(() => {
        this.showSuccess(this.$t('notifications.deleteContactFromGroup', { name: group.groupName }));
      });
    },

    async sendMessageToGroup(group) {
      this.setActiveConversationId(null);
      this.$root.$emit('clear-contact-selection');
      await this.$nextTick();

      group.contacts.forEach(contact => {
        this.$root.$emit('add-contact-to-contact-selection', contact);
      });

      await this.$nextTick();
      this.$root.$emit('focus-conversation-input');
    },

    async sendGroupMessageToGroup(group) {
      this.setActiveConversationId(null);
      this.$root.$emit('clear-contact-selection');
      this.$root.$emit('toggle-group-message-mode');
      await this.$nextTick();

      group.contacts.forEach(contact => {
        this.$root.$emit('add-contact-to-contact-selection', contact);
      });

      await this.$nextTick();
      if(group.contacts.length > 1){
        this.$root.$emit('toggle-group-message-mode', true);
      }
      this.$root.$emit('focus-conversation-input');
    },

    shareGroup(group) {
      this.$root.$emit('share-contacts', group.contacts);
    },

    deleteGroup(group) {
      this.deleteUserGroup(group.id).then(() => {
        this.showSuccess(this.$t('notifications.deleteGroup', { name: group.groupName }));
      });
    },

    addContactDialog() {
      this.$root.$emit('open-contact-dialog');
    },

    openContact(contact) {
      if (this.readOnlyList) return;

      this.$root.$emit('close-sidebar');
      this.startConversation(contact);
    },

    onEditContact(contact) {
      this.$root.$emit('open-contact-dialog', contact);
    },

    onDeleteContact(contact) {
      this.deleteUserContact(contact).then(() => {
        this.showSuccess(
          this.$t('notifications.deleteContact', {
            name: contact.displayName
          })
        );
      });
    },

    addFavorite(contact) {
      this.addContactToFavorites(contact).then(() => {
        this.showSuccess(
          this.$t('notifications.favorites.successfullyAdded', {
            name: contact.displayName
          })
        );
      });
    },

    removeFavorite(contact) {
      this.removeContactFromFavorites(contact).then(() => {
        this.showSuccess(
          this.$t('notifications.favorites.successfullyRemoved', {
            name: contact.displayName
          })
        );
      });
    }
  }
};
</script>

<style src="./scoped.scss" lang="scss" scoped />
<style src="./global.scss" lang="scss" />
